// Dialogs for /finance/loan (model/finance/loans.js): a loan editor (its
// portions, and refinancing), the question-led "Add an entry" dialog (a rate
// change, extra repayment, redraw, statement balance or an
// offset deposit / withdrawal / balance), the contribution plan editor (also
// opened from the budget page) and the household people list. Each saves
// through the model, then calls `onSaved()`.

import {
  createCheckboxRow,
  createField,
  createFieldRow,
  createModal,
  createSelectField,
} from '../components/modal.js';
import { setInfoBox } from '../components/info-box.js';
import { createMoneyField } from '../components/money-input.js';
import { createChoiceList } from '../components/choice-list.js';
import { createQuestionFlow } from '../components/question-flow.js';
import { isoDate } from '../model/finance/fy.js';
import { DEPOSIT_PERIODS } from '../model/finance/fund-deposits.js';
import { FREQUENCY_LABELS } from '../model/finance/income-view.js';
import {
  ME,
  createContributionPlan,
  createLoan,
  createLoanEvent,
  createOffsetEntry,
  deleteLoanItem,
  saveLoanPeople,
  updateLoanItem,
} from '../model/finance/loans.js';
import { ENTRY_LABELS } from '../model/finance/loan-view.js';
import { errorHost, reader, showErrors, withHint } from './finance-fields.js';

import './finance-income-editors.css';

export const LOAN_HINTS = Object.freeze({
  repaymentDay:
    'The day of the month your bank charges interest and takes the repayment (in a shorter month, its last day).',
  offset:
    'Whether the offset account reduces the interest on this portion. Usually only variable portions are offset.',
  portions:
    'A split loan has a portion per rate — e.g. a fixed and a variable portion. Most loans have just one.',
  fromOffset:
    'Ticked when the extra repayment was moved out of the offset account; unticked when it came from somewhere else.',
  portion: 'Leave as the whole loan unless the change applied to just one portion.',
  people:
    'Everyone besides you who pays into the offset account. Removing someone keeps their past contributions (shown as “Someone else”).',
  cadence:
    'Your share can follow your paydays (the pay schedule of your job on the income page) or a fixed schedule counted from the start date.',
});

/** The entry types the "Add an entry" dialog offers (loan events, then the offset). */
const ENTRY_OPTIONS = [
  { value: 'rate', description: 'The bank changed the interest rate (and so the repayment)' },
  { value: 'extra', description: 'A lump sum paid off the loan' },
  { value: 'redraw', description: 'Money taken back out of the loan' },
  { value: 'balance', description: 'The loan balance on a statement' },
  { value: 'offset-deposit', description: 'Money put into the offset account' },
  { value: 'offset-withdrawal', description: 'Money taken out of the offset account' },
  { value: 'offset-balance', description: 'The offset balance on a statement' },
].map((o) => ({ ...o, label: ENTRY_LABELS[o.value] }));

const isOffsetType = (type) => typeof type === 'string' && type.startsWith('offset-');

function fillSelect(select, list, value) {
  select.replaceChildren(
    ...list.map((o) => {
      const el = document.createElement('option');
      el.value = o.value;
      el.textContent = o.label;
      return el;
    }),
  );
  select.value = value;
}

function iconButton(icon, label, onClick, { danger = false, disabled = false } = {}) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `icon-button${danger ? ' icon-danger' : ''}`;
  b.title = label;
  b.setAttribute('aria-label', label);
  b.disabled = disabled;
  const i = document.createElement('i');
  i.className = `fa-solid ${icon}`;
  i.setAttribute('aria-hidden', 'true');
  b.append(i);
  b.addEventListener('click', onClick);
  return b;
}

/** A positive % a year (≤ 100) from a text input, or null + an error. */
function readRate(r, input) {
  const n = Number(input.value.trim().replace('%', ''));
  if (!input.value.trim() || !Number.isFinite(n) || n < 0 || n > 100) {
    r.errors.push('The interest rate must be a percentage a year, e.g. 6.04.');
    return null;
  }
  return n;
}

async function saving(errors, fn) {
  try {
    await fn();
    return true;
  } catch (err) {
    console.error('Saving failed:', err);
    setInfoBox(errors, 'error', 'Could not save — please try again.');
    return false;
  }
}

// ─── Loan editor ─────────────────────────────────────────────────────────────

/**
 * Add / edit a loan (name, lender, settlement, repayment day, portions), or
 * refinance one: a new loan `refinancedFrom` the old, which closes on the new
 * one's settlement date.
 * @param {{ getState: () => { doc, userId }, onSaved: () => Promise<void> }} ctx
 */
export function createLoanEditor({ getState, onSaved }) {
  let editing = null;
  let refinancing = null;
  let portions = [];
  const modal = createModal({
    title: 'Add a loan',
    className: 'income-dialog loan-dialog',
    submitLabel: 'Save',
    keepDraftOnDismiss: true,
    onSubmit: save,
  });
  const help = document.createElement('p');
  help.className = 'modal-help';
  const name = createField({ label: 'Name', name: 'loanName', id: 'loan-name', required: true });
  const lender = createField({ label: 'Lender', name: 'loanLender', id: 'loan-lender' });
  const settled = createField({
    label: 'Settlement date',
    name: 'loanSettled',
    id: 'loan-settled',
    type: 'date',
    required: true,
  });
  const day = createField({
    label: 'Repayment day',
    name: 'loanDay',
    id: 'loan-day',
    type: 'number',
    min: 1,
    max: 31,
    step: 1,
    required: true,
  });
  withHint(day.field, LOAN_HINTS.repaymentDay);
  const closed = createField({
    label: 'Closed on',
    name: 'loanClosed',
    id: 'loan-closed',
    type: 'date',
  });
  const notes = createField({ label: 'Notes', name: 'loanNotes', id: 'loan-notes' });
  const portionsTitle = document.createElement('p');
  portionsTitle.className = 'field-caption';
  portionsTitle.textContent = 'Portions';
  withHint(portionsTitle, LOAN_HINTS.portions);
  const list = document.createElement('ol');
  list.className = 'finance-edit-rows loan-portion-rows';
  const add = document.createElement('button');
  add.type = 'button';
  add.className = 'button-secondary';
  add.textContent = 'Add a portion';
  add.addEventListener('click', () => {
    portions.push(newPortion(portions.length));
    renderPortions();
    list.querySelector('li:last-child input')?.focus();
  });
  const errors = errorHost();
  modal.body.append(
    help,
    createFieldRow([name.field, lender.field]),
    createFieldRow([settled.field, day.field]),
    closed.field,
    portionsTitle,
    list,
    add,
    notes.field,
    errors,
  );

  function newPortion(i, principal = null) {
    return {
      id: `p${Date.now().toString(36)}${i}`,
      name: '',
      type: 'variable',
      principal,
      termYears: 30,
      offset: true,
    };
  }

  function renderPortions() {
    list.replaceChildren(
      ...portions.map((p, i) => {
        const li = document.createElement('li');
        li.className = 'finance-edit-row';
        const pName = createField({
          label: 'Portion name',
          name: `portionName${i}`,
          id: `loan-portion-name-${i}`,
          placeholder: p.type === 'fixed' ? 'Fixed' : 'Variable',
        });
        pName.input.value = p.name;
        pName.input.addEventListener('input', () => (p.name = pName.input.value));
        const type = createSelectField({
          label: 'Rate type',
          name: `portionType${i}`,
          id: `loan-portion-type-${i}`,
        });
        fillSelect(
          type.select,
          [
            { value: 'variable', label: 'Variable' },
            { value: 'fixed', label: 'Fixed' },
          ],
          p.type,
        );
        const principal = createMoneyField({
          label: 'Amount borrowed',
          name: `portionPrincipal${i}`,
          id: `loan-portion-principal-${i}`,
        });
        // Keep what was typed across a re-render (adding / removing a portion).
        if (p.principalText !== undefined) principal.input.value = p.principalText;
        else principal.setValue(p.principal);
        principal.input.addEventListener('input', () => (p.principalText = principal.input.value));
        p.principalInput = principal.input;
        const term = createField({
          label: 'Term (years)',
          name: `portionTerm${i}`,
          id: `loan-portion-term-${i}`,
          type: 'number',
          min: 1,
          max: 40,
          step: 1,
        });
        term.input.value = String(p.termYears);
        term.input.addEventListener('input', () => (p.termYears = term.input.value));
        p.termInput = term.input;
        const offset = createCheckboxRow({
          label: 'Offset applies',
          name: `portionOffset${i}`,
          id: `loan-portion-offset-${i}`,
          checked: p.offset,
        });
        withHint(offset.field, LOAN_HINTS.offset);
        offset.input.addEventListener('change', (e) => (p.offset = e.target.checked));
        type.select.addEventListener('change', () => {
          p.type = type.select.value;
          p.offset = p.type === 'variable';
          offset.input.checked = p.offset;
          pName.input.placeholder = p.type === 'fixed' ? 'Fixed' : 'Variable';
        });
        const tools = document.createElement('div');
        tools.className = 'finance-edit-row-tools';
        tools.append(
          iconButton(
            'fa-trash',
            `Remove the ${p.name || 'portion'}`,
            () => {
              portions.splice(i, 1);
              renderPortions();
            },
            { danger: true, disabled: portions.length === 1 },
          ),
        );
        li.append(
          createFieldRow([pName.field, type.field]),
          createFieldRow([principal.field, term.field]),
          offset.field,
          tools,
        );
        return li;
      }),
    );
  }

  /**
   * Open for a stored `loan`, or (null) a new one; `refinanceOf` (a loan) +
   * `owingCents` (its balance today) start a refinance.
   */
  function open(loan = null, { refinanceOf = null, owingCents = null } = {}) {
    const key = loan?.itemId ?? (refinanceOf ? `refinance:${refinanceOf.itemId}` : 'new');
    if (modal.resumeDraft(key)) return;
    editing = loan;
    refinancing = refinanceOf;
    setInfoBox(errors, null);
    const today = isoDate(new Date());
    if (loan) {
      modal.setTitle(`Edit ${loan.name}`);
      help.textContent = 'Changes apply to the whole history of this loan.';
    } else if (refinanceOf) {
      modal.setTitle(`Refinance ${refinanceOf.name}`);
      help.textContent = `The new loan pays out ${refinanceOf.name}, which closes on the new loan’s settlement date. The offset account carries over.`;
    } else {
      modal.setTitle('Add a loan');
      help.textContent =
        'Start with the loan as it was at settlement — record later rate changes and extra repayments as entries (repayments are worked out from the rate), and refinance from the loan’s menu.';
    }
    const base = loan ?? refinanceOf;
    name.input.value = loan?.name ?? (refinanceOf ? `${refinanceOf.name} (refinanced)` : '');
    lender.input.value = base?.lender ?? '';
    settled.input.value = loan?.settlementDate ?? today;
    day.input.value = String(loan?.repaymentDay ?? refinanceOf?.repaymentDay ?? 1);
    closed.field.hidden = !loan;
    closed.input.value = loan?.closedOn ?? '';
    notes.input.value = loan?.notes ?? '';
    portions = loan
      ? loan.portions.map((p) => ({ ...p }))
      : [
          newPortion(
            0,
            owingCents !== null && owingCents !== undefined
              ? { amount: owingCents, currency: 'AUD' }
              : null,
          ),
        ];
    renderPortions();
    modal.open({ draftKey: key });
  }

  async function save() {
    const r = reader();
    if (!name.input.value.trim()) r.errors.push('The name is required.');
    if (!settled.input.value) r.errors.push('The settlement date is required.');
    const repaymentDay = r.int(day.input, 'The repayment day', { min: 1, max: 31 });
    const stored = portions.map((p, i) => {
      const label =
        portions.length > 1 ? `Portion ${i + 1}’s amount borrowed` : 'The amount borrowed';
      const principal = r.amount(p.principalInput, label, { required: true, currency: 'AUD' });
      const termYears = r.int(p.termInput, `Portion ${i + 1}’s term`, { min: 1, max: 40 });
      return { id: p.id, name: p.name, type: p.type, principal, termYears, offset: p.offset };
    });
    if (closed.input.value && closed.input.value <= settled.input.value) {
      r.errors.push('The closing date must be after the settlement date.');
    }
    if (showErrors(errors, r.errors)) return false;
    const value = {
      name: name.input.value,
      lender: lender.input.value,
      settlementDate: settled.input.value,
      repaymentDay,
      portions: stored,
      refinancedFrom: editing?.refinancedFrom ?? refinancing?.itemId ?? null,
      closedOn: editing ? closed.input.value || null : null,
      notes: notes.input.value,
    };
    const { doc, userId } = getState();
    const ok = await saving(errors, async () => {
      if (editing) {
        await updateLoanItem(doc, userId, editing.itemId, value);
        return;
      }
      await createLoan(doc, userId, value);
      if (refinancing) {
        const { itemId, ...old } = refinancing;
        await updateLoanItem(doc, userId, itemId, { ...old, closedOn: value.settlementDate });
      }
    });
    if (!ok) return false;
    modal.close();
    await onSaved();
    return true;
  }

  return { open };
}

// ─── Entry dialog ────────────────────────────────────────────────────────────

/**
 * "Add an entry": what happened (choice) → when (+ which loan / portion) → the
 * figure. Editing opens with every question revealed; changing an entry's
 * loan or its kind (loan ↔ offset) re-creates it.
 * @param {{ getState: () => { doc, userId, loans }, onSaved: () => Promise<void> }} ctx
 */
export function createEntryEditor({ getState, onSaved }) {
  let editing = null;
  let type = null;
  const modal = createModal({
    title: 'Add an entry',
    className: 'income-dialog loan-entry-dialog',
    submitLabel: 'Save',
    keepDraftOnDismiss: true,
    onSubmit: save,
  });
  const typeList = createChoiceList({
    ariaLabel: 'What happened?',
    options: ENTRY_OPTIONS,
    onChange: (v) => {
      type = v;
      syncType();
      flow.advance('type');
      refresh();
    },
  });
  const date = createField({
    label: 'Date',
    name: 'entryDate',
    id: 'loan-entry-date',
    type: 'date',
    required: true,
  });
  const loanSel = createSelectField({ label: 'Loan', name: 'entryLoan', id: 'loan-entry-loan' });
  const portionSel = createSelectField({
    label: 'Portion',
    name: 'entryPortion',
    id: 'loan-entry-portion',
  });
  withHint(portionSel.field, LOAN_HINTS.portion);
  loanSel.select.addEventListener('change', () => fillPortions(null));
  const rate = createField({
    label: 'Interest rate (% a year)',
    name: 'entryRate',
    id: 'loan-entry-rate',
    inputmode: 'decimal',
    placeholder: 'e.g. 6.04',
  });
  const amount = createMoneyField({
    label: 'Amount',
    name: 'entryAmount',
    id: 'loan-entry-amount',
  });
  const fromOffset = createCheckboxRow({
    label: 'Taken out of the offset account',
    name: 'entryFromOffset',
    id: 'loan-entry-from-offset',
    checked: true,
  });
  withHint(fromOffset.field, LOAN_HINTS.fromOffset);
  const note = createField({ label: 'Note', name: 'entryNote', id: 'loan-entry-note' });
  const errors = errorHost();
  const amountValid = () => {
    const raw = amount.input.value.trim();
    return !!raw && Number(raw.replace(/,/g, '')) > 0;
  };
  const flow = createQuestionFlow({
    steps: [
      {
        id: 'type',
        question: 'What happened?',
        children: [typeList.el],
        answered: () => !!type,
      },
      {
        id: 'when',
        question: 'When was it?',
        children: [date.field, createFieldRow([loanSel.field, portionSel.field])],
        answered: () => !!date.input.value,
      },
      {
        id: 'figure',
        question: 'How much?',
        children: [rate.field, amount.field, fromOffset.field, note.field],
        answered: () => (type === 'rate' ? rate.input.value.trim() !== '' : amountValid()),
      },
    ],
    onChange: refresh,
  });
  modal.body.append(flow.el, errors);

  function fillPortions(value) {
    const loan = getState().loans.find((l) => l.itemId === loanSel.select.value);
    const options = [
      { value: '', label: 'Whole loan' },
      ...(loan?.portions ?? []).map((p) => ({ value: p.id, label: p.name })),
    ];
    fillSelect(portionSel.select, options, value ?? '');
    portionSel.field.hidden = isOffsetType(type) || (loan?.portions.length ?? 0) < 2;
  }

  function syncType() {
    const offsetType = isOffsetType(type);
    loanSel.field.hidden = offsetType || getState().loans.length < 2;
    fillPortions(portionSel.select.value || null);
    rate.field.hidden = type !== 'rate';
    amount.field.hidden = type === 'rate';
    fromOffset.field.hidden = type !== 'extra';
    flow.section('figure').querySelector('.question__title').textContent =
      type === 'rate' ? 'What is the new rate?' : 'How much?';
  }

  function refresh() {
    modal.setSubmitEnabled(flow.isComplete());
  }

  /** Open for a history item (`historyItems` row) or (null) a new entry. */
  function open(item = null) {
    const key = item?.itemId ?? 'new';
    if (modal.resumeDraft(key)) return;
    editing = item;
    const { loans } = getState();
    setInfoBox(errors, null);
    modal.setTitle(item ? `Edit ${ENTRY_LABELS[item.type].toLowerCase()}` : 'Add an entry');
    type = item?.type ?? null;
    typeList.setValue(type);
    const row = item?.row ?? null;
    date.input.value = row?.date ?? isoDate(new Date());
    const current = loans.filter((l) => !l.closedOn).at(-1) ?? loans.at(-1);
    fillSelect(
      loanSel.select,
      loans.map((l) => ({ value: l.itemId, label: l.name })),
      row?.loanRef ?? current?.itemId ?? '',
    );
    portionSel.select.value = '';
    rate.input.value = row?.rate !== undefined ? String(row.rate) : '';
    amount.setValue(row?.amount ?? null);
    fromOffset.input.checked = row?.fromOffset !== false;
    note.input.value = row?.note ?? '';
    syncType();
    fillPortions(row?.portionId ?? null);
    if (item) flow.revealAll();
    else flow.start();
    refresh();
    modal.open({ draftKey: key });
  }

  async function save() {
    const r = reader();
    if (!type) r.errors.push('Choose what happened.');
    if (!date.input.value) r.errors.push('The date is required.');
    const value = { date: date.input.value, note: note.input.value };
    if (type === 'rate') value.rate = readRate(r, rate.input);
    else value.amount = r.amount(amount.input, 'The amount', { required: true, currency: 'AUD' });
    if (showErrors(errors, r.errors)) return false;
    const { doc, userId } = getState();
    const offsetType = isOffsetType(type);
    const loanRef = loanSel.select.value;
    const event = offsetType
      ? { ...value, kind: type.slice('offset-'.length) }
      : {
          ...value,
          type,
          portionId: portionSel.field.hidden ? null : portionSel.select.value || null,
          fromOffset: fromOffset.input.checked,
        };
    const sameRow =
      editing &&
      (editing.source === 'offset') === offsetType &&
      (offsetType || editing.row.loanRef === loanRef);
    const ok = await saving(errors, async () => {
      if (sameRow) {
        await updateLoanItem(doc, userId, editing.itemId, event);
        return;
      }
      if (offsetType) await createOffsetEntry(doc, userId, event);
      else await createLoanEvent(doc, userId, loanRef, event);
      if (editing) await deleteLoanItem(doc, userId, editing.itemId);
    });
    if (!ok) return false;
    modal.close();
    await onSaved();
    return true;
  }

  return { open };
}

// ─── Contribution plans ──────────────────────────────────────────────────────

const MY_CADENCES = [
  { value: '', label: 'Each payday' },
  ...DEPOSIT_PERIODS.map((p) => ({ value: p, label: FREQUENCY_LABELS[p] })),
];
const OTHER_CADENCES = DEPOSIT_PERIODS.map((p) => ({ value: p, label: FREQUENCY_LABELS[p] }));

/**
 * The contribution plan editor: from a date, how much each person pays into
 * the offset and how often (in force until the next plan). Every amount blank
 * = a pause. Shared with the budget page.
 * @param {{ getState: () => { doc, userId, people }, onSaved: () => Promise<void> }} ctx
 */
export function createContributionEditor({ getState, onSaved }) {
  let editing = null;
  let rows = [];
  const modal = createModal({
    title: 'Add contributions',
    className: 'income-dialog loan-plan-dialog',
    submitLabel: 'Save',
    keepDraftOnDismiss: true,
    onSubmit: save,
  });
  const help = document.createElement('p');
  help.className = 'modal-help';
  help.textContent =
    'What each person pays into the offset account (repayments come out of it). Applies from this date until your next change. Leave every amount blank to pause.';
  const from = createField({
    label: 'Starting',
    name: 'planFrom',
    id: 'loan-plan-from',
    type: 'date',
    required: true,
  });
  const list = document.createElement('ol');
  list.className = 'finance-edit-rows loan-plan-rows';
  const errors = errorHost();
  modal.body.append(help, from.field, list, errors);

  /**
   * Open to edit a stored `plan`, or (null) for a new one — starting today,
   * prefilled from `basedOn` (the latest plan) as a "Change contributions".
   */
  function open(plan = null, { basedOn = null } = {}) {
    const key = plan?.itemId ?? 'new';
    if (modal.resumeDraft(key)) return;
    editing = plan;
    const source = plan ?? basedOn;
    modal.setTitle(
      plan ? 'Edit contributions' : basedOn ? 'Change contributions' : 'Add contributions',
    );
    setInfoBox(errors, null);
    from.input.value = plan?.from ?? isoDate(new Date());
    const { people } = getState();
    const known = [{ id: ME, name: 'You' }, ...people];
    // Anyone a plan names who's since been removed stays editable.
    for (const id of Object.keys(source?.amounts ?? {})) {
      if (!known.some((p) => p.id === id)) known.push({ id, name: 'Someone else' });
    }
    rows = known.map((person, i) => {
      const share = source?.amounts[person.id] ?? null;
      const li = document.createElement('li');
      li.className = 'finance-edit-row';
      const label = person.id === ME ? 'You pay' : `${person.name} pays`;
      const f = createMoneyField({ label, name: `planAmount${i}`, id: `loan-plan-amount-${i}` });
      f.setValue(share?.amount ?? null);
      const cadence = createSelectField({
        label: 'How often',
        name: `planPeriod${i}`,
        id: `loan-plan-period-${i}`,
      });
      if (person.id === ME) {
        fillSelect(cadence.select, MY_CADENCES, share?.period ?? '');
        withHint(cadence.field, LOAN_HINTS.cadence);
      } else fillSelect(cadence.select, OTHER_CADENCES, share?.period ?? 'fortnightly');
      li.append(createFieldRow([f.field, cadence.field]));
      return { person, label, f, cadence, li };
    });
    list.replaceChildren(...rows.map((row) => row.li));
    modal.open({ draftKey: key });
  }

  async function save() {
    const r = reader();
    if (!from.input.value) r.errors.push('The start date is required.');
    const amounts = {};
    for (const { person, label, f, cadence } of rows) {
      const m = r.amount(f.input, label, { currency: 'AUD' });
      if (m && m.amount > 0)
        amounts[person.id] = { amount: m, period: cadence.select.value || null };
    }
    if (showErrors(errors, r.errors)) return false;
    const plan = { from: from.input.value, amounts };
    const { doc, userId } = getState();
    const ok = await saving(errors, () =>
      editing
        ? updateLoanItem(doc, userId, editing.itemId, plan)
        : createContributionPlan(doc, userId, plan),
    );
    if (!ok) return false;
    modal.close();
    await onSaved();
    return true;
  }

  return { open };
}

// ─── People ──────────────────────────────────────────────────────────────────

/**
 * The household people list (everyone besides you who contributes).
 * @param {{ getState: () => { doc, userId, people }, onSaved: () => Promise<void> }} ctx
 */
export function createPeopleEditor({ getState, onSaved }) {
  let rows = [];
  const modal = createModal({
    title: 'Household people',
    className: 'income-dialog loan-people-dialog',
    submitLabel: 'Save',
    onSubmit: save,
  });
  const help = document.createElement('p');
  help.className = 'modal-help';
  help.textContent = LOAN_HINTS.people;
  const list = document.createElement('ol');
  list.className = 'finance-edit-rows loan-people-rows';
  const add = document.createElement('button');
  add.type = 'button';
  add.className = 'button-secondary';
  add.textContent = 'Add a person';
  add.addEventListener('click', () => {
    rows.push({ id: crypto.randomUUID(), name: '' });
    render();
    list.querySelector('li:last-child input')?.focus();
  });
  const errors = errorHost();
  modal.body.append(help, list, add, errors);

  function render() {
    list.replaceChildren(
      ...rows.map((row, i) => {
        const li = document.createElement('li');
        li.className = 'finance-edit-row';
        const f = createField({ label: 'Name', name: `personName${i}`, id: `loan-person-${i}` });
        f.input.value = row.name;
        f.input.addEventListener('input', () => (row.name = f.input.value));
        const tools = document.createElement('div');
        tools.className = 'finance-edit-row-tools';
        tools.append(
          iconButton(
            'fa-trash',
            `Remove ${row.name || 'this person'}`,
            () => {
              rows.splice(i, 1);
              render();
            },
            { danger: true },
          ),
        );
        li.append(f.field, tools);
        return li;
      }),
    );
  }

  function open() {
    rows = getState().people.map((p) => ({ ...p }));
    if (!rows.length) rows.push({ id: crypto.randomUUID(), name: '' });
    setInfoBox(errors, null);
    render();
    modal.open();
  }

  async function save() {
    const { doc, userId } = getState();
    const ok = await saving(errors, () =>
      saveLoanPeople(
        doc,
        userId,
        rows.filter((r) => r.name.trim()),
      ),
    );
    if (!ok) return false;
    modal.close();
    await onSaved();
    return true;
  }

  return { open };
}
